import { useCallback, useRef, ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Upload } from 'lucide-react';

import { cn } from '@/lib/utils';

import { useFileDrop } from '../model/use-file-drop';

interface FileUploadProps {
  accept?: string;
  multiple?: boolean;
  onFilesSelected?: (files: FileList) => void;
  className?: string;
  disabled?: boolean;
}

export function FileUpload({ accept, multiple = true, onFilesSelected, className, disabled = false }: FileUploadProps) {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const { isDragging, handleDragOver, handleDragLeave, handleDrop } = useFileDrop({
    onFilesSelected,
    disabled
  });

  const handleFileChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        onFilesSelected?.(e.target.files);
      }
    },
    [onFilesSelected]
  );

  const handleClick = useCallback(() => {
    if (!disabled) {
      fileInputRef.current?.click();
    }
  }, [disabled]);

  const uploadClasses = cn(
    'flex flex-col items-center justify-center gap-4 px-6 py-8',
    'bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg',
    'cursor-pointer transition-all duration-200 select-none',
    'hover:bg-gray-100 hover:border-primary-600',
    'focus-visible:outline-2 focus-visible:outline-primary-500 focus-visible:outline-offset-2',
    {
      'bg-primary-50 border-primary-500': isDragging,
      'opacity-50 cursor-not-allowed': disabled
    },
    className
  );

  return (
    <div
      className={uploadClasses}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={handleClick}
      role="button"
      tabIndex={disabled ? -1 : 0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          handleClick();
        }
      }}>
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        accept={accept}
        multiple={multiple}
        onChange={handleFileChange}
        disabled={disabled}
        aria-hidden="true"
      />

      <Upload className="text-gray-600" size={40} />

      <div className="flex flex-col items-center gap-2">
        <span className="text-sm text-gray-600 text-center">{t('gen.drage.file.label')}</span>
      </div>
    </div>
  );
}
